import { useState } from 'react'
import { Image as ImageIcon, MoreHorizontal } from 'lucide-react'

/**
 * LA SOIRÉE CONCURRENTE — PRIMITIVE CANONIQUE.
 *
 * Un `market_event` s'affiche PARTOUT avec ce composant : fiche d'un
 * établissement surveillé, « Autour de Rennes », contexte concurrentiel d'une
 * soirée du Live Club. Trois markups indépendants pour le même objet métier,
 * c'est trois façons de dériver — et c'est exactement ce qui produisait des
 * lignes de tableau ici et des cartes là.
 *
 * DEUX AXES, JAMAIS CONFONDUS :
 *
 *   FIABILITÉ  « croit-on que c'est vrai ? »   → provenance, preuve, lecture
 *   IMPACT     « est-ce que ça peut nous gêner ? » → même nuit, distance, poids
 *
 * Une Story publiée par le compte officiel d'un concurrent, avec affiche et
 * date claire, est TRÈS FIABLE — même si elle a un impact faible parce que
 * nous ne jouons pas ce soir-là. L'inverse existe aussi.
 *
 * HIÉRARCHIE VISUELLE, du plus fort au plus discret :
 *   date → titre → horaire → line-up → lieu → provenance.
 * Huit informations au même niveau ne se lisent pas.
 */

export interface MarketEventLike {
  id: string
  title: string
  starts_at: string
  ends_at?: string | null
  venue_id?: string | null
  venue_name: string | null
  city?: string | null
  address?: string | null
  artists?: string[]
  /** La nuit ANNONCÉE — celle du flyer. C'est elle qu'on affiche. */
  business_night_date?: string | null
  /** Faux quand aucune heure n'était lisible : pas de fausse précision. */
  time_known?: boolean
  distance_km?: number | null
  relevance_level: 'FORT' | 'MOYEN' | 'FAIBLE'
  confidence: string
  status?: string
  sources: { name: string; url?: string | null; seen_at: string }[]
  last_verified_at?: string
  first_seen_at?: string
  capture_content_id?: string | null
  hidden?: boolean
}

/* ─────────────────────────── vocabulaire ──────────────────────────────── */

/**
 * FIABILITÉ — dite en français, sur quatre niveaux.
 *
 * `CONFIRMED` ne veut pas dire « la soirée aura lieu » : il veut dire « nous
 * avons de bonnes raisons de croire cette information ». La nuance est celle
 * qui sépare une affiche officielle d'une rumeur de forum.
 */
const FIABILITE: Record<string, { label: string; ton: 'sure' | 'ok' | 'doute' }> = {
  CONFIRMED: { label: 'Source sûre', ton: 'sure' },
  LIKELY: { label: 'Source fiable', ton: 'ok' },
  UNCERTAIN: { label: 'À confirmer', ton: 'doute' },
}

/** IMPACT — ce que ça peut nous coûter, pas ce qu'on en croit. */
const IMPACT: Record<string, { label: string; ton: 'fort' | 'moyen' | 'faible' }> = {
  FORT: { label: 'Impact fort', ton: 'fort' },
  MOYEN: { label: 'Impact moyen', ton: 'moyen' },
  FAIBLE: { label: 'Impact faible', ton: 'faible' },
}

/**
 * « VEN. 28 AOÛT » — la date telle qu'on la dit, EN HEURE DE PARIS.
 *
 * Le formatage par défaut suit le fuseau du navigateur. Sur un poste en UTC,
 * une soirée du 28 à 00 h s'affichait « JEU. 27 » à côté d'un horaire
 * « 00:00 » calculé, lui, en heure de Paris : deux informations qui se
 * contredisaient dans la même carte.
 */
export function dateLongueFr(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    weekday: 'short', day: 'numeric', month: 'long', timeZone: 'Europe/Paris',
  }).toUpperCase()
}

/**
 * LA DATE QU'ON MONTRE EST CELLE DU FLYER.
 *
 * Une affiche « VEN. 28 AOÛT · 00H–06H » ouvre, en temps civil, le samedi 29
 * à minuit. Afficher « SAM. 29 » serait exact et pourtant faux : personne ne
 * reconnaîtrait la soirée annoncée.
 *
 * L'instant civil reste la vérité pour les CALCULS — chevauchement, même
 * nuit, concurrence. Il n'est jamais l'étiquette.
 */
export function dateAffichee(e: { business_night_date?: string | null; starts_at: string }): string {
  if (e.business_night_date) {
    // Midi UTC : à l'abri de tout glissement de fuseau dans un sens ou l'autre.
    return dateLongueFr(`${e.business_night_date}T12:00:00Z`)
  }
  return dateLongueFr(e.starts_at)
}
export function heureFr(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR',
    { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })
}
/** « il y a 10 min » — la fraîcheur compte plus que l'horodatage. */
export function ageFr(iso: string | null | undefined): string | null {
  if (!iso) return null
  const delta = Date.now() - new Date(iso).getTime()
  if (delta < 0) return 'à l’instant'
  const min = Math.floor(delta / 60_000)
  if (min < 1) return 'à l’instant'
  if (min < 60) return `il y a ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `il y a ${h} h`
  const j = Math.floor(h / 24)
  return j === 1 ? 'hier' : `il y a ${j} j`
}

/**
 * La provenance, en une ligne courte et lisible.
 *
 * Plusieurs sources sur la même soirée ne donnent PAS plusieurs cartes : on
 * annonce le recoupement, qui est en soi un gage de fiabilité (§76).
 */
function provenanceFr(sources: MarketEventLike['sources']): string | null {
  if (sources.length === 0) return null
  if (sources.length > 1) return `${sources.length} sources concordantes`
  const n = sources[0]!.name
  // « Instagram — Story @lespaceclub » se lit mieux en « Story Instagram ·
  // @lespaceclub » : le canal d'abord, le compte ensuite.
  const story = /Story/i.test(n)
  const handle = /@([\w.]+)/.exec(n)?.[0] ?? null
  if (story && handle) return `Story Instagram · ${handle}`
  return n
}

/* ─────────────────────────── la carte ─────────────────────────────────── */

export function MarketEventCard({ e, densite = 'standard', onOpen, onCapture, actions }: {
  e: MarketEventLike
  /** `compact` reste une CARTE : plus dense, jamais une ligne de tableau. */
  densite?: 'standard' | 'compact'
  onOpen?: (() => void) | undefined
  onCapture?: ((contentId: string) => void) | undefined
  /** Menu de modération, rendu par l'appelant — secondaire, jamais permanent. */
  actions?: React.ReactNode
}) {
  const [menu, setMenu] = useState(false)
  const fia = FIABILITE[e.confidence] ?? FIABILITE['LIKELY']!
  const imp = IMPACT[e.relevance_level] ?? IMPACT['FAIBLE']!
  const prov = provenanceFr(e.sources)
  const age = ageFr(e.first_seen_at ?? e.last_verified_at)
  const compact = densite === 'compact'

  // §10 — une donnée absente se masque, elle ne s'affiche pas « null ».
  const lieu = [e.venue_name, e.city,
    // `!= null` et non `!== null` : un champ ABSENT doit être écarté comme
    // un champ vide. Le distinguer a coûté un écran blanc.
    e.distance_km != null ? `${e.distance_km.toLocaleString('fr-FR')} km` : null,
  ].filter(Boolean).join(' · ')

  const corps = (
    <>
      <div className="mev__head">
        <span className={`mev__badge mev__badge--fia-${fia.ton}`}>{fia.label}</span>
        <span className={`mev__badge mev__badge--imp-${imp.ton}`}>{imp.label}</span>
      </div>

      <div className="mev__body">
        <div className="mev__text">
          <p className="mev__date">{dateAffichee(e)}</p>
          <h4 className="mev__title">{e.title}</h4>
          {/* §126 — sans heure lisible sur l'affiche, on n'en invente pas. */}
          {e.time_known !== false ? (
            <p className="mev__hours">
              {heureFr(e.starts_at)}{e.ends_at ? ` → ${heureFr(e.ends_at)}` : ''}
            </p>
          ) : null}
          {(e.artists?.length ?? 0) > 0 ? (
            <p className="mev__artists">{e.artists!.join(' · ')}</p>
          ) : null}
          {lieu ? <p className="mev__place">{lieu}</p> : null}
          {prov || age ? (
            <p className="mev__prov">{[prov, age].filter(Boolean).join(' · ')}</p>
          ) : null}
        </div>

        {/* §11/§75 — sans capture, la mise en page se rééquilibre : pas de
            placeholder géant, pas de colonne vide. */}
        {e.capture_content_id ? (
          <button type="button" className="mev__thumb"
            onClick={(ev) => { ev.stopPropagation(); onCapture?.(e.capture_content_id!) }}
            aria-label="Voir l’affiche analysée">
            <img src={`/api/v1/market/social/stories/${e.capture_content_id}/capture`}
              alt="" loading="lazy" />
            <span className="mev__thumb__icon" aria-hidden="true"><ImageIcon size={13} /></span>
          </button>
        ) : null}
      </div>
    </>
  )

  return (
    <article
      className={`mev${compact ? ' mev--compact' : ''}${e.hidden ? ' mev--hidden' : ''}`}
      aria-label={`${e.title} — ${dateAffichee(e)}`}
    >
      {onOpen ? (
        <button type="button" className="mev__open" onClick={onOpen}
          aria-label={`Détail : ${e.title}`}>{corps}</button>
      ) : corps}

      {actions ? (
        <div className="mev__menu">
          {/* §25-§26 — le menu ne NAVIGUE plus : la carte entière ouvre le
              détail. Il ne porte que des actions secondaires. */}
          <button type="button" className="mev__menu__btn" aria-haspopup="menu"
            aria-expanded={menu} aria-label="Actions sur cet événement"
            onClick={(ev) => { ev.stopPropagation(); setMenu((o) => !o) }}>
            <MoreHorizontal size={16} aria-hidden="true" />
          </button>
          {menu ? (
            <>
              <div className="mev__menu__scrim" onClick={() => setMenu(false)} aria-hidden="true" />
              <div className="mev__menu__pop lc-floating" role="menu"
                onClick={() => setMenu(false)}>{actions}</div>
            </>
          ) : null}
        </div>
      ) : null}

      {e.hidden ? <span className="mev__hiddenTag">Masqué</span> : null}
    </article>
  )
}
